/**
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 **/
import React, { ReactElement, useEffect, useCallback, useState, useRef } from 'react';
import { Browsers, UaParserDom } from '@userzoomplatform/ua-parser';
import { useSplitTreatments } from '@splitsoftware/splitio-react';
// eslint-disable-next-line import/no-extraneous-dependencies
import debounce from 'lodash.debounce';
import { useWebExtension } from '../../../providers/web-extension';
import {
	checkTimedOut,
	extensionUpdated,
	extensionUninstalled,
	setRequirements,
	setExtension,
	ExtensionRequirements,
} from '../../../providers/web-extension/actions';
import { ExtensionStatus, ExtensionEvents, ExtensionCheckTimeOut } from '../../../utils/constants';
import { FeatureFlags } from '../../../hocs/with-split/with-split';

/* eslint-disable no-console */

const Extension = (): ReactElement => {
	const { state, dispatch } = useWebExtension();
	const [device] = useState(() => {
		const uaParser = new UaParserDom(window.navigator.userAgent);
		return uaParser.getDevice();
	});
	const { treatments, isReady } = useSplitTreatments({
		names: [FeatureFlags.NewTasks, FeatureFlags.WebExtensions, FeatureFlags.WebExtensionsV2, FeatureFlags.WebExtensionsBeta],
	});

	const checkTimeout = useRef<NodeJS.Timeout>();
	const reCheckTimeout = useRef<NodeJS.Timeout>();
	const uninstallCheckTimeout = useRef<NodeJS.Timeout>();

	const { extension, requirements } = state;

	useEffect(() => {
		const browserName = device.browser?.name === Browsers.Firefox ? Browsers.Firefox : Browsers.Chrome;
		if (treatments && browserName) {
			let splitConfig: ExtensionRequirements;
			if (state.useBeta) {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				splitConfig = JSON.parse((treatments[FeatureFlags.WebExtensionsBeta] as any).config)[browserName];
			} else {
				const treatment = treatments[FeatureFlags.NewTasks].treatment === 'on' ? FeatureFlags.WebExtensions : FeatureFlags.WebExtensionsV2;
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				splitConfig = JSON.parse((treatments[treatment] as any).config)[browserName];
			}
			dispatch(setRequirements({ requirements: splitConfig, browserName }));
		}
	}, [treatments, dispatch, device.browser?.name, state.useBeta]);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const onExtensionVersionResponse = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		debounce((event: any) => {
			clearTimeout(checkTimeout.current);
			clearTimeout(reCheckTimeout.current);
			dispatch(setExtension({ ...JSON.parse(event.detail), browserName: device.browser?.name ?? '' }));
		}, 300),
		[device.browser?.name, dispatch]
	);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const onExtensionUpdated = useCallback(
		debounce((e: unknown) => {
			dispatch(extensionUpdated());
		}, 300),
		[dispatch]
	);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const onExtensionUninstalled = useCallback(
		debounce((e: unknown) => {
			clearTimeout(checkTimeout.current);
			clearTimeout(uninstallCheckTimeout.current);
			dispatch(extensionUninstalled());
		}, 300),
		[dispatch]
	);

	useEffect(() => {
		if (requirements?.minVersion && extension) {
			if (state.useDev) {
				return;
			}
			if (state.status === ExtensionStatus.FirefoxV2Outdated) {
				clearTimeout(checkTimeout.current);
				clearTimeout(reCheckTimeout.current);
			} else if (state.status === ExtensionStatus.Outdated) {
				if (device?.browser?.name === Browsers.Firefox) {
					window.dispatchEvent(new Event(ExtensionEvents.UninstallExtension));
					/* The following timeouts are to handle the scenario where we don't receive the "onExtensionUninstalled"
					event. Re-checking its existence to ensure that in fact, it has been uninstalled */
					checkTimeout.current = setTimeout(() => {
						dispatch(checkTimedOut());
					}, ExtensionCheckTimeOut);
					uninstallCheckTimeout.current = setTimeout(() => {
						window.dispatchEvent(new Event(ExtensionEvents.CheckExtensionVersion));
					}, ExtensionCheckTimeOut / 2);
				} else if (state.requirements.id === state.extension?.id) {
					window.dispatchEvent(new Event(ExtensionEvents.UpdateExtension));
				} else {
					window.dispatchEvent(new Event(ExtensionEvents.UninstallExtension));
				}
			}
		}
	}, [device?.browser?.name, dispatch, extension, requirements, state.extension?.id, state.requirements.id, state.status, state.useDev]);

	useEffect(() => {
		if (treatments[FeatureFlags.WebExtensions]?.config && isReady) {
			window.addEventListener(ExtensionEvents.ExtensionVersionResponse, onExtensionVersionResponse);
			window.addEventListener(ExtensionEvents.ExtensionUpdated, onExtensionUpdated);
			window.addEventListener(ExtensionEvents.ExtensionUninstalled, onExtensionUninstalled);

			checkTimeout.current = setTimeout(() => {
				dispatch(checkTimedOut());
			}, ExtensionCheckTimeOut);

			reCheckTimeout.current = setTimeout(() => {
				window.dispatchEvent(new Event(ExtensionEvents.CheckExtensionVersion));
			}, ExtensionCheckTimeOut - 1000);

			window.dispatchEvent(new Event(ExtensionEvents.CheckExtensionVersion));
		}

		return () => {
			window.removeEventListener(ExtensionEvents.ExtensionVersionResponse, onExtensionVersionResponse);
			window.removeEventListener(ExtensionEvents.ExtensionUpdated, onExtensionUpdated);
			window.removeEventListener(ExtensionEvents.ExtensionUninstalled, onExtensionUninstalled);
			clearTimeout(checkTimeout.current);
			clearTimeout(reCheckTimeout.current);
		};
	}, [dispatch, onExtensionVersionResponse, treatments, isReady, onExtensionUpdated, onExtensionUninstalled]);

	return <div />;
};

export default Extension;
